import type { CheckinGuideConfig, PublicCheckinGuide } from '../api.checkinGuide';
import { withoutUneditedSamples } from '../pages/apartmentEdit/guideSamples';

/** Shown instead of a real entry code: a preview must never reveal or mint one. */
export const PREVIEW_PIN_PLACEHOLDER = '••••••';

const blankToNull = (v: string | null | undefined): string | null => (v && v.trim() ? v.trim() : null);

/** Same rule as the server's `resolveLocalizedText`: English wins only when it was actually written. */
const localized = (primary: string | null | undefined, en: string | null | undefined, english: boolean): string | null =>
  blankToNull(english ? (blankToNull(en) ?? primary) : primary);

const isoDay = (offset: number): string => {
  const d = new Date();
  d.setDate(d.getDate() + offset);
  return d.toISOString().slice(0, 10);
};

/**
 * Builds, in the browser, the payload the guest Guide would receive for the manager's current
 * (possibly unsaved) editor values. Nothing here calls the server: no reservation, token, PIN
 * generation, message or integration is involved, which is what makes the preview side-effect free.
 *
 * Deliberate differences from the real thing, all disclosed in the preview banner:
 * demo dates; the reveal-time gate is not applied; a smart-lock PIN is a placeholder; weather is
 * not fetched; deposit and upsell panels are not shown.
 */
export const buildPreviewGuide = (
  draft: CheckinGuideConfig,
  apartmentName: string,
  language: 'cs' | 'en',
): PublicCheckinGuide => {
  const c = withoutUneditedSamples(draft);
  const en = language === 'en';
  const lock = c.keyAccessType === 'TTLOCK';
  const untranslated = en && (
    (!!blankToNull(c.addressRemark) && !blankToNull(c.addressRemarkEn))
    || (!!blankToNull(c.keyRemark) && !blankToNull(c.keyRemarkEn))
    || (!!blankToNull(c.parkingInfo) && !blankToNull(c.parkingInfoEn))
    || (!!blankToNull(c.detailedGuide) && !blankToNull(c.detailedGuideEn))
    || (!!blankToNull(c.attentionNotes) && !blankToNull(c.attentionNotesEn))
    || (!!blankToNull(c.checkoutInstructions) && !blankToNull(c.checkoutInstructionsEn))
  );
  return {
    apartmentName,
    status: 'READY',
    checkInDate: isoDay(0),
    checkOutDate: isoDay(2),
    checkInTime: blankToNull(c.checkInTime),
    checkOutTime: blankToNull(c.checkOutTime),
    showAddress: c.showAddress,
    showKeyAccess: c.showKeyAccess,
    showWifi: c.showWifi,
    showParking: c.showParking,
    showContact: c.showContact,
    showDetailedGuide: c.showDetailedGuide,
    showPhotos: c.showPhotos,
    showOtherLinks: c.showOtherLinks,
    showAttentionNotes: c.showAttentionNotes,
    showCheckoutInstructions: c.showCheckoutInstructions,
    address: blankToNull(c.address),
    addressRemark: localized(c.addressRemark, c.addressRemarkEn, en),
    keyAccessType: c.keyAccessType,
    keyCode: lock ? null : blankToNull(c.keyCode),
    keyCodeHidden: false,
    keyCodeVisibleFromTime: null,
    keyRemark: localized(c.keyRemark, c.keyRemarkEn, en),
    wifiName: blankToNull(c.wifiName),
    wifiPassword: blankToNull(c.wifiPassword),
    parkingInfo: localized(c.parkingInfo, c.parkingInfoEn, en),
    parkingPrice: blankToNull(c.parkingPrice),
    hostContactName: blankToNull(c.hostContactName),
    hostContactPhone: blankToNull(c.hostContactPhone),
    hostContactEmail: blankToNull(c.hostContactEmail),
    detailedGuide: localized(c.detailedGuide, c.detailedGuideEn, en),
    attentionNotes: localized(c.attentionNotes, c.attentionNotesEn, en),
    checkoutInstructions: localized(c.checkoutInstructions, c.checkoutInstructionsEn, en),
    otherLinks: (c.otherLinks ?? []).filter(l => l.url && l.url.trim()),
    photos: c.photos ?? [],
    ttlockPinCode: lock && c.showKeyAccess ? PREVIEW_PIN_PLACEHOLDER : null,
    ttlockPinHidden: false,
    ttlockPinVisibleFromTime: null,
    showWeather: c.showWeather,
    weatherDays: [],
    contentPartiallyUntranslated: untranslated,
  };
};
